import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Card,
    Col,
    Row,
    Table,
    Tag,
    Button,
    Select,
    DatePicker,
    Popover,
    Typography,
    Dropdown,
    Input,
} from 'antd';
import { DownloadOutlined, DownOutlined } from '@ant-design/icons';
import SpeciesBirdIdentificationMap from '../map/SpeciesBirdIdentificationMap';
import { useKeycloak } from '@react-keycloak/web';
import { fetchEviaDetections, fetchThraceDetections } from '../../externalAPI/clickhouse';
import { Line, LineChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';

const { Option } = Select;
const { RangePicker } = DatePicker;

// Define interfaces for data structures
export interface BirdData {
    key: number;
    sensorId: string;
    timestamp: string;
    commonName: string;
    scientificName: string;
    identificationProbability: number;
    conservationStatus?: string;
    protectionStatus?: string[];
    controlOrStudyarea?: string;
}

export interface SensorStats {
    sensorId: string;
    totalRecordings: number;
    uniqueSpecies: number;
    avgProbability: number;
    lastRecording: string;
    recordings: BirdData[];
}

function useDebounce(value: any, delay: number) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

const SpeciesBirdGreekTemporalAnalysis: React.FC = () => {
    // State management for UI interactions and data
    const [selectedSensorId, setSelectedSensorId] = useState<string | null>(null);
    const [hoveredSensorId, setHoveredSensorId] = useState<string | null>(null);
    const [monitoringData, setMonitoringData] = useState<BirdData[]>([]);
    const [loading, setLoading] = useState(true);
    const [greeceLocation, setGreeceLocation] = useState<string | null>(null);
    const [eviaData, setEviaData] = useState<BirdData[]>([]);
    const [thraceData, setThraceData] = useState<BirdData[]>([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });
    // New state for sensor ID filter
    const [sensorIdFilter, setSensorIdFilter] = useState<string>('');
    const debouncedSensorIdFilter = useDebounce(sensorIdFilter, 300);

    // Filter states - updated filters
    const [selectedControlOrStudyArea, setSelectedControlOrStudyArea] = useState<string | undefined>(undefined);
    const [dateRange, setDateRange] = useState<any>(null);
    const [timeScale, setTimeScale] = useState<'hourly' | 'daily' | 'weekly' | 'monthly'>('daily');
    const [selectedSpecies, setSelectedSpecies] = useState<string[]>([]);

    // Keycloak integration for user case study
    const { keycloak } = useKeycloak();
    const userCaseStudy = keycloak.idTokenParsed?.case_study as string | undefined;
    const chartRef = useRef(null);

    // Effect to load initial data based on user's case study
    useEffect(() => {
        const loadInitialData = async () => {
            setLoading(true); // Set loading to true before fetching data
            try {
                if (userCaseStudy === 'Greece') {
                    const promises = [];
                    // Conditionally add the correct promise to the array
                    if (greeceLocation === 'Evia') {
                        promises.push(fetchEviaDetections().then((evia: BirdData[]) => setEviaData(evia)));
                    } else if (greeceLocation === 'Thrace') {
                        promises.push(fetchThraceDetections().then((thrace: BirdData[]) => setThraceData(thrace)));
                    }

                    // Only await if there are promises to run
                    if (promises.length > 0) {
                        await Promise.allSettled(promises);
                    }
                }
            } catch (error) {
                console.error('Error loading bird data:', error);
            } finally {
                setLoading(false); // Set loading to false after data is fetched or an error occurs
            }
        };

        loadInitialData();
    }, [userCaseStudy, greeceLocation]);

    // Effect to set monitoring data based on selected Greece location
    useEffect(() => {
        if (userCaseStudy === 'Greece') {
            if (greeceLocation === 'Evia') {
                setMonitoringData(eviaData);
            } else if (greeceLocation === 'Thrace') {
                setMonitoringData(thraceData);
            } else {
                setMonitoringData([]);
            }
        } else {
            setMonitoringData([]);
        }
    }, [greeceLocation, eviaData, thraceData, userCaseStudy]);

    // Memoized filtered data based on selected filters
    const filteredData = useMemo(() => {
        if (!monitoringData || monitoringData.length === 0) return [];
        let filtered = [...monitoringData];

        // Sensor ID Filter - Using debounced value
        if (debouncedSensorIdFilter) {
            filtered = filtered.filter(
                (item) =>
                    typeof item.sensorId === 'string' &&
                    item.sensorId.toLowerCase().includes(debouncedSensorIdFilter.toLowerCase())
            );
        }

        // Control/Study area filter
        if (selectedControlOrStudyArea) {
            filtered = filtered.filter(
                (item) => item.controlOrStudyarea === selectedControlOrStudyArea
            );
        }

        // Date range filter
        if (dateRange && dateRange.length === 2) {
            const [startDate, endDate] = dateRange;
            filtered = filtered.filter((item) => {
                const itemDate = new Date(item.timestamp);
                return itemDate >= startDate.toDate() && itemDate <= endDate.toDate();
            });
        }

        return filtered;
    }, [monitoringData, selectedControlOrStudyArea, dateRange, debouncedSensorIdFilter]);

    // Memoized sensor statistics calculation
    const sensorStats = useMemo((): Record<string, SensorStats> => {
        const stats: Record<string, SensorStats> = {};

        filteredData.forEach((item) => {
            if (!stats[item.sensorId]) {
                stats[item.sensorId] = {
                    sensorId: item.sensorId,
                    totalRecordings: 0,
                    uniqueSpecies: 0,
                    avgProbability: 0,
                    lastRecording: '',
                    recordings: [],
                };
            }
            stats[item.sensorId].recordings.push(item);
        });

        // Calculate derived statistics for each sensor
        Object.values(stats).forEach((stat) => {
            stat.totalRecordings = stat.recordings.length;
            stat.uniqueSpecies = new Set(stat.recordings.map((r) => r.commonName)).size;
            stat.avgProbability = Math.round(
                stat.recordings.reduce((sum, r) => sum + r.identificationProbability, 0) / stat.totalRecordings,
            );
            stat.lastRecording =
                stat.recordings.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0]
                    ?.timestamp || '';
        });

        return stats;
    }, [filteredData]);

    // Get unique species for the species filter
    const uniqueSpecies = useMemo(() => {
        const speciesSet = new Set<string>();
        monitoringData.forEach((item) => {
            const speciesName = item.commonName && item.commonName.trim() !== ''
                ? item.commonName
                : item.scientificName;
            if (speciesName) {
                speciesSet.add(speciesName);
            }
        });
        return Array.from(speciesSet).sort();
    }, [monitoringData]);

    // Get unique values for the control/study area filter
    const uniqueControlOrStudyAreas = useMemo(
        () => [...new Set(monitoringData.map((item) => item.controlOrStudyarea))].filter(Boolean).sort() as string[],
        [monitoringData],
    );

    // Helper function to format date based on time scale
    const formatDateByTimeScale = (date: Date, scale: string) => {
        switch (scale) {
            case 'hourly':
                return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:00`;
            case 'daily':
                return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            case 'weekly':
                const weekStart = new Date(date);
                weekStart.setDate(date.getDate() - date.getDay());
                return `Week of ${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`;
            case 'monthly':
                return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            default:
                return date.toISOString().split('T')[0];
        }
    };

    // Memoized chart data for temporal analysis
    const chartData = useMemo(() => {
        if (filteredData.length === 0) return [];

        const timeGroups: { [key: string]: { [species: string]: number } } = {};

        filteredData.forEach((item) => {
            const date = new Date(item.timestamp);
            const timeKey = formatDateByTimeScale(date, timeScale);
            const speciesName = item.commonName && item.commonName.trim() !== ''
                ? item.commonName
                : item.scientificName;

            if (!timeGroups[timeKey]) {
                timeGroups[timeKey] = {};
            }

            if (selectedSpecies.length === 0) {
                // If no species selected, count total detections
                timeGroups[timeKey]['Total Detections'] = (timeGroups[timeKey]['Total Detections'] || 0) + 1;
            } else {
                // If species selected, count detections per species
                if (selectedSpecies.includes(speciesName)) {
                    timeGroups[timeKey][speciesName] = (timeGroups[timeKey][speciesName] || 0) + 1;
                }
            }
        });

        // Convert to array format for chart
        const chartArray = Object.entries(timeGroups)
            .map(([timeKey, speciesData]) => ({
                time: timeKey,
                ...speciesData
            }))
            .sort((a, b) => a.time.localeCompare(b.time));

        return chartArray;
    }, [filteredData, timeScale, selectedSpecies]);

    // Generate colors for different species lines
    const speciesColors = useMemo(() => {
        const colors = [
            '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00ff00',
            '#0088fe', '#00c49f', '#ffbb28', '#ff8042', '#8dd1e1',
            '#d084d0', '#87ceeb', '#ffd700', '#ff6347', '#9acd32',
            '#dda0dd', '#98fb98', '#f0e68c', '#ff1493', '#00ced1'
        ];

        const colorMap: { [key: string]: string } = {};
        if (selectedSpecies.length === 0) {
            colorMap['Total Detections'] = '#8884d8';
        } else {
            selectedSpecies.forEach((species, index) => {
                colorMap[species] = colors[index % colors.length];
            });
        }

        return colorMap;
    }, [selectedSpecies]);

    // Event handlers for map and table interactions
    const handleSensorSelect = useCallback((sensorId: string | null) => {
        setSelectedSensorId(sensorId);
    }, []);

    const handleSensorHover = useCallback((sensorId: string | null) => {
        setHoveredSensorId(sensorId);
    }, []);

    const handleRowClick = useCallback(
        (record: BirdData) => {
            const newSelection = selectedSensorId === record.sensorId ? null : record.sensorId;
            setSelectedSensorId(newSelection);
        },
        [selectedSensorId],
    );

    // Function to clear all active filters
    const clearFilters = useCallback(() => {
        setSelectedControlOrStudyArea(undefined);
        setDateRange(null);
        setTimeScale('daily');
        setSelectedSensorId('');
        setSensorIdFilter('');
        setSelectedSpecies([]);
    }, []);

    // Table columns configuration
    const columns = [
        {
            title: 'Sensor ID',
            dataIndex: 'sensorId',
            key: 'sensorId',
            sorter: (a: BirdData, b: BirdData) => a.sensorId.localeCompare(b.sensorId),
        },
        {
            title: 'Timestamp',
            dataIndex: 'timestamp',
            key: 'timestamp',
            sorter: (a: BirdData, b: BirdData) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
            render: (text: string) => {
                const date = new Date(text);
                if (isNaN(date.getTime())) {
                    return 'Invalid Date';
                }
                const day = date.getDate();
                const month = date.toLocaleDateString('en-US', { month: 'long' });
                const year = date.getFullYear();
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                const seconds = String(date.getSeconds()).padStart(2, '0');
                return `${day} ${month} ${year} ${hours}:${minutes}:${seconds}`;
            },
        },
        {
            title: 'Common Name',
            dataIndex: 'commonName',
            key: 'commonName',
            sorter: (a: BirdData, b: BirdData) => (a.commonName ?? '').localeCompare(b.commonName ?? ''),
        },
        {
            title: 'Scientific Name',
            dataIndex: 'scientificName',
            key: 'scientificName',
            sorter: (a: BirdData, b: BirdData) => a.scientificName.localeCompare(b.scientificName),
        },
        {
            title: 'Probability',
            dataIndex: 'identificationProbability',
            key: 'identificationProbability',
            sorter: (a: BirdData, b: BirdData) => a.identificationProbability - b.identificationProbability,
            render: (probability: number) => `${probability.toFixed(2)}%`,
        },
        {
            title: 'Control/Study Area',
            dataIndex: 'controlOrStudyarea',
            key: 'controlOrStudyarea',
            sorter: (a: BirdData, b: BirdData) => (a.controlOrStudyarea ?? '').localeCompare(b.controlOrStudyarea ?? ''),
            render: (area: string | undefined) => area || 'N/A',
        },
    ];

    // Handlers for the Greece location dropdown
    const handleGreeceLocationChange = useCallback((value: string | null) => {
        setGreeceLocation(value);
    }, []);

    const locations = [
        {
            key: 'Evia',
            label: 'Evia',
        },
        {
            key: 'Thrace',
            label: 'Thrace',
        },
    ];

    const handleMenuClick = (e: { key: string }) => {
        handleGreeceLocationChange(e.key);
    };

    const menuProps = {
        items: locations,
        onClick: handleMenuClick,
    };

    const exportToCsv = () => {
        // Get the headers from the columns array
        const headers = columns.map(col => col.title);

        // Map the filtered data to an array of arrays, with each inner array representing a row
        const rows = filteredData.map(record => columns.map(col => (record as any)[col.dataIndex]));

        // Combine headers and rows
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.join(','))
        ].join('\n');

        // Create a temporary link element to trigger the download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.download = `detection_records_${new Date().toISOString().slice(0, 19).replace(/T|:/g, '-')}.csv`;
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const downloadChart = async () => {
        if (!chartRef.current) {
            console.error('Chart container not found');
            return;
        }

        try {
            // Capture the chart as a canvas
            const canvas = await html2canvas(chartRef.current, {
                backgroundColor: null,
                scale: 2, // For higher resolution
                useCORS: true,
                logging: false,
                ignoreElements: (element) => {
                    // Ignore any elements that shouldn't be in the export
                    return element.classList.contains('ignore-export');
                }
            });

            // Convert canvas to blob
            canvas.toBlob((blob) => {
                if (blob) {
                    // Save the blob as a PNG file
                    saveAs(blob, `temporal_analysis_chart_${new Date().toISOString().slice(0, 19).replace(/T|:/g, '-')}.png`);
                }
            }, 'image/png');
        } catch (error) {
            console.error('Error downloading chart:', error);
        }
    };


    return (
        <div style={{ minHeight: '100vh' }}>
            <h1 style={{ color: '#047857', fontSize: '1.5rem', fontWeight: '600' }}>
                Home &gt; Species &gt; Temporal Analysis
            </h1>

            <Row gutter={[16, 10]}>
                {/* "Select a location" component */}
                {userCaseStudy === 'Greece' && (
                    <Col xs={24} sm={12} md={8} lg={6} style={{ paddingLeft: '8px', paddingRight: '8px', marginTop: '1rem', paddingBottom: '0.5rem' }}>
                        <Card style={{ borderRadius: '0.5rem' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                <Typography.Text strong style={{ color: '#374151' }}>Select a location:</Typography.Text>
                                <Dropdown menu={menuProps}>
                                    <Button
                                        style={{
                                            width: '100%',
                                            textAlign: 'left',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            borderRadius: '0.375rem',
                                            border: '1px solid #d1d5db',
                                            padding: '0.5rem 1rem',
                                            backgroundColor: '#ffffff',
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                                        onFocus={(e) => {
                                            e.currentTarget.style.outline = '2px solid transparent';
                                            e.currentTarget.style.outlineOffset = '2px';
                                            e.currentTarget.style.boxShadow = '0 0 0 2px #ffffff, 0 0 0 4px #22c55e';
                                        }}
                                        onBlur={(e) => e.currentTarget.style.boxShadow = 'none'}
                                    >
                                        {greeceLocation || '--- Select ---'}
                                        <DownOutlined id='SelectAreaGreeceBirdsId' style={{ marginLeft: '0.5rem' }} />
                                    </Button>
                                </Dropdown>
                            </div>
                        </Card>
                    </Col>
                )}

                {/* Map Section */}
                <Col span={24} style={{ paddingLeft: '8px', paddingRight: '8px', flexBasis: '100%', maxWidth: '100%' }}>
                    <Card
                        title={
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '1.125rem', fontWeight: '600', color: '#1f2937' }}>
                                <span>Sensor Network Map</span>
                            </div>
                        }
                        style={{ marginBottom: '1rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06)' }}
                    >
                        <SpeciesBirdIdentificationMap
                            sensorStats={sensorStats}
                            selectedSensorId={selectedSensorId}
                            hoveredSensorId={hoveredSensorId}
                            onSensorSelect={handleSensorSelect}
                            onSensorHover={handleSensorHover}
                            location={greeceLocation}
                        />
                        <Typography.Paragraph
                            style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#6b7280' }}
                        >
                            Installed wind-turbines data were sourced from the publicly accessible geoportal of the Hellenic Regulatory Authority for Waste, Energy and Water (<a href="https://geo.rae.gr/" target="_blank" rel="noopener noreferrer">https://geo.rae.gr/</a>). Accessed Feb. 2025.
                        </Typography.Paragraph>
                        <Typography.Paragraph style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                            Learn more about AvesEcho:<Typography.Link href='https://guarden.org/technologies/avesecho/' target='_blank'> https://guarden.org/technologies/avesecho/</Typography.Link>
                        </Typography.Paragraph>
                    </Card>
                </Col>

                {/* Data Table Section */}
                <Col span={24} style={{ paddingLeft: '8px', paddingRight: '8px' }}>
                    <div style={{ marginTop: '1rem', borderRadius: '0.5rem', boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)', backgroundColor: '#fff' }}>
                        <div style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '1.125rem', fontWeight: '600', color: '#1f2937' }}>
                                <span>Detection Records </span>
                                <span style={{ fontSize: '0.875rem', fontWeight: '400', color: '#4b5563' }}>
                                    {filteredData.length} of {monitoringData.length} records
                                    {selectedSensorId && ` (Sensor: ${selectedSensorId})`}
                                </span>
                            </div>
                        </div>
                        {/* Updated Filters Section */}
                        <div style={{
                            marginBottom: '1rem',
                            borderRadius: '0.5rem',
                            backgroundColor: '#f9fafb',
                            boxShadow: 'inset 0 2px 4px 0 rgba(0,0,0,0.06)',
                            padding: '0.75rem'
                        }}>
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: '1rem'
                            }}>
                                {/* Sensor ID Filter */}
                                <div style={{ flex: '1 1 250px', minWidth: '250px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                                        <Typography.Text strong style={{ color: '#374151', fontSize: '0.875rem' }}>Sensor ID</Typography.Text>
                                        <Input
                                            id='SensorIdFilterBirdsId'
                                            placeholder='Search by Sensor ID...'
                                            value={sensorIdFilter}
                                            onChange={(e) => setSensorIdFilter(e.target.value)}
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                </div>
                                {/* Control/Study Area Filter */}
                                <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                                        <span style={{ fontWeight: '700', color: '#4b5563', fontSize: '0.875rem' }}>Control/Study Area</span>
                                        <Select
                                            id='ControlOrStudyAreaBirdsId'
                                            placeholder='Select an Area'
                                            value={selectedControlOrStudyArea}
                                            onChange={setSelectedControlOrStudyArea}
                                            style={{ width: '100%' }}
                                            allowClear
                                        >
                                            {uniqueControlOrStudyAreas.map((area) => (
                                                <Option key={area} value={area}>{area}</Option>
                                            ))}
                                        </Select>
                                    </div>
                                </div>

                                {/* Date Range Filter */}
                                <div style={{ flex: '1 1 300px', minWidth: '300px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                                        <span style={{ fontWeight: '700', color: '#4b5563', fontSize: '0.875rem' }}>Date Range</span>
                                        <RangePicker
                                            id="FilterDateBirdsId"
                                            value={dateRange}
                                            onChange={setDateRange}
                                            showTime
                                            format='YYYY-MM-DD HH:mm'
                                            style={{ width: '100%' }}
                                            disabledDate={(current) => {
                                                // Define min/max dates (months are 0-indexed)
                                                const minDate = new Date(2024, 1, 1); // Feb 1, 2024
                                                const maxDate = new Date(2024, 6, 31); // July 31, 2024

                                                if (!current) return false;
                                                // current may be Dayjs or Moment; convert to native Date for comparison
                                                const currentDate = (current as any).toDate ? (current as any).toDate() : new Date(current as unknown as string);
                                                return currentDate < minDate || currentDate > maxDate;
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Time Scale Filter */}
                                <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                                        <span style={{ fontWeight: '700', color: '#4b5563', fontSize: '0.875rem' }}>Time Scale</span>
                                        <Select
                                            id='TimeScaleBirdsId'
                                            placeholder='Select time scale'
                                            value={timeScale}
                                            onChange={setTimeScale}
                                            style={{ width: '100%' }}
                                        >
                                            <Option value="hourly">Hourly</Option>
                                            <Option value="daily">Daily</Option>
                                            <Option value="weekly">Weekly</Option>
                                            <Option value="monthly">Monthly</Option>
                                        </Select>
                                    </div>
                                </div>

                                {/* Species Selection Filter */}
                                <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                                        <span style={{ fontWeight: '700', color: '#4b5563', fontSize: '0.875rem' }}>Species Selection</span>
                                        <Select
                                            id='SpeciesSelectionBirdsId'
                                            mode="multiple"
                                            placeholder='Select species'
                                            value={selectedSpecies}
                                            onChange={setSelectedSpecies}
                                            style={{ width: '100%' }}
                                            allowClear
                                            maxTagCount={2}
                                            maxTagTextLength={20}
                                        >
                                            {uniqueSpecies.map((species) => (
                                                <Option key={species} value={species}>{species}</Option>
                                            ))}
                                        </Select>
                                    </div>
                                </div>

                                {/* Clear All Button */}
                                <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                                    <Button
                                        onClick={clearFilters}
                                        type='link'
                                        size='large'
                                        style={{ height: '32px', color: '#2563eb', fontSize: '1rem' }}
                                    >
                                        Clear All
                                    </Button>
                                </div>
                                {/* Export to CSV Button */}
                                <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                                    <Button
                                        onClick={exportToCsv}
                                        type='primary'
                                        icon={<DownloadOutlined />}
                                        size='large'
                                        style={{
                                            height: '32px',
                                            backgroundColor: '#329619', // The new background color
                                            borderColor: '#329619',     // Optional: Keep border color consistent
                                        }}
                                    >
                                        Export to CSV
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <Table
                            id='TableBirdsId'
                            columns={columns}
                            dataSource={filteredData}
                            rowKey='key'
                            loading={loading}
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                showSizeChanger: true,
                                pageSizeOptions: ['10', '20', '50', '100'],
                                showQuickJumper: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
                                onChange: (page, pageSize) => {
                                    setPagination({ current: page, pageSize });
                                },
                            }}
                            rowClassName={(record) => (selectedSensorId === record.sensorId ? 'table-row-selected' : '')}
                            onRow={(record) => ({
                                onMouseEnter: () => setHoveredSensorId(record.sensorId),
                                onMouseLeave: () => setHoveredSensorId(null),
                                onClick: () => handleRowClick(record),
                                style: {
                                    cursor: 'pointer',
                                    backgroundColor:
                                        selectedSensorId === record.sensorId
                                            ? '#f6ffed'
                                            : hoveredSensorId === record.sensorId
                                                ? '#f6ffed'
                                                : 'transparent',
                                },
                            })}
                            style={{ borderRadius: '0.5rem', overflow: 'hidden' }}
                        />
                    </div>
                </Col>

                {/* Temporal Analysis Line Chart Section */}
                <Col span={24} style={{ paddingLeft: '8px', paddingRight: '8px', marginTop: '1rem' }}>
                    <Card
                        title={
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '1.125rem', fontWeight: '600', color: '#1f2937' }}>
                                <span>
                                    {selectedSpecies.length === 0
                                        ? `Total Detections Over Time (${timeScale})`
                                        : `Species Detections Over Time (${timeScale})`
                                    }
                                </span>
                                <span style={{ fontSize: '0.875rem', fontWeight: '400', color: '#4b5563' }}>
                                    {selectedSpecies.length === 0
                                        ? `Total detection count aggregated by ${timeScale} intervals`
                                        : `Detection count per selected species aggregated by ${timeScale} intervals`
                                    }
                                </span>
                            </div>
                        }
                        style={{ marginTop: '1rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06)' }}
                    >
                        {chartData.length > 0 ? (
                            <div style={{ flexBasis: '100%', maxWidth: '100%', padding: '1rem', backgroundColor: '#fff', borderRadius: '0.5rem', boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)' }}>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                                    <Button
                                        onClick={downloadChart}
                                        type="primary"
                                        icon={<DownloadOutlined />}
                                        style={{ height: '32px', backgroundColor: '#329619', borderColor: '#329619' }}
                                    >
                                        Download Chart
                                    </Button>
                                </div>
                                <ResponsiveContainer width="100%" height={700} ref={chartRef}>
                                    <LineChart
                                        data={chartData}
                                        margin={{
                                            top: 45,
                                            left: 20,
                                            bottom: 60,
                                        }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="time"
                                            angle={-45}
                                            textAnchor="end"
                                            height={80}
                                            label={{
                                                value: `Time (${timeScale})`,
                                                offset: -30,
                                                position: 'insideBottom',
                                            }}
                                        />
                                        <YAxis
                                            label={{
                                                value: 'Number of Detections',
                                                angle: -90,
                                                position: 'insideLeft',
                                            }}
                                        />
                                        <Tooltip
                                            formatter={(value, name) => [value, name]}
                                            labelFormatter={(label) => `${timeScale === 'hourly' ? 'Hour' : timeScale === 'daily' ? 'Date' : timeScale === 'weekly' ? 'Week' : 'Month'}: ${label}`}
                                        />
                                        <Legend />
                                        {selectedSpecies.length === 0 ? (
                                            <Line
                                                type="monotone"
                                                dataKey="Total Detections"
                                                stroke={speciesColors['Total Detections']}
                                                strokeWidth={2}
                                                dot={{ r: 4 }}
                                                activeDot={{ r: 6 }}
                                                name="Total Detections"
                                            />
                                        ) : (
                                            selectedSpecies.map((species) => (
                                                <Line
                                                    key={species}
                                                    type="monotone"
                                                    dataKey={species}
                                                    stroke={speciesColors[species]}
                                                    strokeWidth={2}
                                                    dot={{ r: 4 }}
                                                    activeDot={{ r: 6 }}
                                                    name={species}
                                                    connectNulls={false}
                                                />
                                            ))
                                        )}
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <Typography.Text disabled style={{ color: '#6b7280' }}>
                                No data available for chart. Apply filters or load data.
                            </Typography.Text>
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );

}

export { SpeciesBirdGreekTemporalAnalysis };
